import { createContext, useContext, useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import type { ChatConversation, ChatMessage } from "@/types/chat";
import { updateConversationTitle } from "@/api/conversations";

// @ts-ignore - ActionCable types not available
import { createConsumer } from "@rails/actioncable";

// @ts-ignore - Honeybadger is a global
declare const Honeybadger: any;

type ChatKind = "research_assistant";
type ConversationByKind = Record<ChatKind, ChatConversation | null>;

interface ChatContextType {
  currentConversation: ChatConversation | null;
  currentConversationByKind: ConversationByKind;
  currentKind: ChatKind;
  setCurrentKind: React.Dispatch<React.SetStateAction<ChatKind>>;
  getCurrentConversation: (kind: ChatKind) => ChatConversation | null;
  setCurrentConversationForKind: (
    kind: ChatKind,
    conversation: ChatConversation | null | ((prev: ChatConversation | null) => ChatConversation | null),
  ) => void;
  loadConversation: (id: number, kind?: ChatKind) => Promise<void>;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  updateTitle: (id: number, title: string, kind?: ChatKind) => Promise<void>;
  chats: ChatConversation[];
  setChats: (chats: ChatConversation[] | ((prevChats: ChatConversation[]) => ChatConversation[])) => void;
  isLoading: boolean;
  createChat: (
    municipalityId: number,
    addressData?: Record<string, unknown>,
    kind?: ChatKind,
  ) => Promise<ChatConversation>;
  isCreatingChat: boolean;
  sendPrompt: (prompt: string, selectedDocumentIds?: number[], kind?: ChatKind) => Promise<void>;
  isSendingPrompt: boolean;
  isStreamingResponse: boolean;
  progressMessage: string;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const fetchChats = async (): Promise<ChatConversation[]> => {
  const response = await fetch("/api/v1/chats");
  const data = await response.json();
  return data.sort(
    (a: ChatConversation, b: ChatConversation) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
};

const fetchConversation = async (id: number): Promise<ChatConversation> => {
  const response = await fetch(`/api/v1/chats/${id}`);
  return response.json();
};


export function ChatProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [currentConversationByKind, setCurrentConversationByKind] = useState<ConversationByKind>({
    research_assistant: null,
  });
  const [currentKind, setCurrentKind] = useState<ChatKind>("research_assistant");
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [isStreamingResponse, setIsStreamingResponse] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const activeConversationIdRef = useRef<Record<ChatKind, number | null>>({
    research_assistant: null,
  });

  const { data: chats = [], isLoading } = useQuery({
    queryKey: ["chats"],
    queryFn: fetchChats,
  });

  // TODO: BEGIN - This is not a concern for the ChatContext. Move this out to most likely the sidebar component.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  // TODO: END - This is not a concern for the ChatContext. Move this out to most likely the sidebar component.

  const currentConversation = currentConversationByKind[currentKind];

  const getCurrentConversation = (kind: ChatKind) => currentConversationByKind[kind];

  const setCurrentConversationForKind = (
    kind: ChatKind,
    conversation: ChatConversation | null | ((prev: ChatConversation | null) => ChatConversation | null),
  ) => {
    setCurrentConversationByKind((prev) => ({
      ...prev,
      [kind]: typeof conversation === "function" ? conversation(prev[kind]) : conversation,
    }));
  };

  const setCurrentConversationForKindIfActive = (
    kind: ChatKind,
    conversationId: number,
    updater: ChatConversation | ((prev: ChatConversation | null) => ChatConversation | null),
  ) => {
    if (activeConversationIdRef.current[kind] !== conversationId) {
      return;
    }
    setCurrentConversationByKind((prev) => {
      if (activeConversationIdRef.current[kind] !== conversationId) {
        return prev;
      }
      const nextConversation = typeof updater === "function" ? updater(prev[kind]) : updater;
      return {
        ...prev,
        [kind]: nextConversation,
      };
    });
  };

  // Subscribe to progress updates
  useEffect(() => {
    if (currentConversation?.id && isStreamingResponse) {
      const subscription = createConsumer().subscriptions.create(
        {
          channel: "ChatProgressChannel",
          chat_id: currentConversation.id,
        },
        {
          received: (data: any) => {
            if (data.type === "progress") {
              setProgressMessage(data.message);
            }
          },
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [currentConversation?.id, isStreamingResponse]);

  // Subscribe to chat updates (title changes, etc.)
  useEffect(() => {
    if (currentConversation?.id) {
      type TitleUpdateEvent = {
        type: "title_update";
        chat_id: number;
        title: string;
        timestamp?: string;
      };

      const subscription = createConsumer().subscriptions.create(
        {
          channel: "ChatChannel",
          chat_id: currentConversation.id,
        },
        {
          received: (data: unknown) => {
            const evt = data as TitleUpdateEvent;
            if (evt.type === "title_update" && evt.chat_id && evt.title) {
              // Update the chats list with the new title
              queryClient.setQueryData(["chats"], (oldChats: ChatConversation[] | undefined) => {
                if (!oldChats) return oldChats;
                return oldChats.map((chat) =>
                  chat.id === evt.chat_id ? { ...chat, title: evt.title } : chat
                );
              });
              // Update current conversation if it's the one being updated
              if (currentConversation.id === evt.chat_id) {
                setCurrentConversationByKind((prev) => ({
                  ...prev,
                  [currentKind]: prev[currentKind] ? { ...prev[currentKind], title: evt.title } : null,
                }));
              }
            }
          },
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [currentConversation?.id]);

  const updateTitle = async (id: number, title: string, kind: ChatKind = currentKind) => {
    const updatedConversation = await updateConversationTitle(id, title);
    queryClient.setQueryData(["chat", id], updatedConversation);
    queryClient.setQueryData(["chats"], (oldChats: ChatConversation[] | undefined) => {
      if (!oldChats) return oldChats;
      return oldChats.map((chat) => (chat.id === id ? { ...chat, title } : chat));
    });
    setCurrentConversationForKindIfActive(kind, id, updatedConversation);
  };

  const loadConversation = async (id: number, kind: ChatKind = currentKind) => {
    setCurrentKind(kind);
    activeConversationIdRef.current[kind] = id;
    if (id === -1) {
      setCurrentConversationForKindIfActive(kind, id, {
        id: -1,
        title: "New Conversation",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
        municipality_id: 0,
        discarded_at: null,
        configuration: {
          address_data: {},
          currently_selected_documents: [],
        },
      });
    } else {
      const conversation = await queryClient.fetchQuery({
        queryKey: ["chat", id],
        queryFn: () => fetchConversation(id),
      });
      setCurrentConversationForKindIfActive(kind, id, conversation);
    }
  };

  const createChatMutation = useMutation<
    ChatConversation,
    Error,
    { municipalityId: number; addressData?: Record<string, unknown>; kind?: ChatKind }
  >({
    mutationFn: async ({ municipalityId, addressData = {}, kind }) => {
      const payload = {
        chat: {
          municipality_id: municipalityId,
          ...(kind ? { kind } : {}),
          configuration: {
            address_data: addressData,
          },
        },
      };
      // something in address_data is causing issues
      // const payload = {
      //   chat: {
      //     municipality_id: municipalityId,
      //     configuration: {
      //       address_data: {},
      //     }
      //   }
      // };

      const response = await fetch("/api/v1/chats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to create chat: ${response.statusText}`);
      }

      return response.json() as Promise<ChatConversation>;
    },
    onSuccess: (newChat, variables) => {
      queryClient.setQueryData(["chats"], (oldChats: ChatConversation[] | undefined) => {
        if (!oldChats) {
          return [newChat];
        }

        return [newChat, ...oldChats];
      });
      const kind = variables.kind ?? "research_assistant";
      setCurrentKind(kind);
      activeConversationIdRef.current[kind] = newChat.id;
      setCurrentConversationForKindIfActive(kind, newChat.id, newChat);
    },
    onError: (error) => {
      console.error("Chat creation error:", error);
    },
  });

  const sendPromptMutation = useMutation<
    ChatConversation,
    Error,
    { prompt: string; documentIds?: number[]; kind?: ChatKind }
  >({
    mutationFn: async ({ prompt, documentIds, kind = currentKind }) => {
      const activeConversation = currentConversationByKind[kind];
      if (!activeConversation?.id) {
        throw new Error("No active conversation");
      }
      const conversationId = activeConversation.id;

      setIsStreamingResponse(true);
      setProgressMessage("Sending your message...");

      // Get any selected document IDs from the front-end state
      const chatConfig = activeConversation.configuration || {};
      const selectedDocumentIds = documentIds ?? chatConfig.currently_selected_documents ?? [];

      // Send message with selected document IDs

      // Post the user's message for the current conversation
      const response = await fetch("/api/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          message: {
            content: prompt,
            source: "from_human",
            selected_document_ids: selectedDocumentIds,
            time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          chat_id: conversationId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const newMessage = await response.json();

      // Immediately update the conversation cache with the new user message
      const currentCache = queryClient.getQueryData<ChatConversation>(["chat", conversationId]);
      if (currentCache) {
        const updatedConversation = {
          ...currentCache,
          messages: [...(currentCache.messages || []), newMessage],
        };
        queryClient.setQueryData(["chat", conversationId], updatedConversation);
        setCurrentConversationForKindIfActive(kind, conversationId, updatedConversation);
      }

      // Start polling for bot response
      return new Promise((resolve, reject) => {
        let retryCount = 0;
        const MAX_RETRIES = 120; // Increased from 30 to 120 attempts
        const POLLING_INTERVAL = 2000; // Increased from 1000ms to 2000ms (2 seconds)
        // This will now poll for up to 4 minutes (120 attempts * 2 seconds)

        const pollInterval = setInterval(async () => {
          retryCount++;
          if (retryCount >= MAX_RETRIES) {
            clearInterval(pollInterval);
            setIsStreamingResponse(false);
            setProgressMessage("");
            resolve(newMessage);
            return;
          }

          try {
            const chatResponse = await fetch(`/api/v1/chats/${conversationId}`);
            if (!chatResponse.ok) {
              throw new Error("Failed to fetch chat");
            }

            const chatData = await chatResponse.json();

            // Check if we have a new bot message that was created after our user message
            const hasNewBotMessage = chatData.messages?.some(
              (message: ChatMessage) =>
                message.source === "from_bot" && new Date(message.created_at) > new Date(newMessage.created_at),
            );

            if (hasNewBotMessage) {
              clearInterval(pollInterval);
              // Update the cache and current conversation with the new data
              queryClient.setQueryData(["chat", conversationId], chatData);
              setCurrentConversationForKindIfActive(kind, conversationId, chatData);
              setIsStreamingResponse(false);
              setProgressMessage("");
              resolve(chatData);
              return;
            }
          } catch (error) {
            console.error("Error polling for response:", error);
            Honeybadger.notify(error);
            clearInterval(pollInterval);
            setIsStreamingResponse(false);
            setProgressMessage("");
            reject(error);
          }
        }, POLLING_INTERVAL);

        // Add cleanup function
        return () => {
          clearInterval(pollInterval);
          setIsStreamingResponse(false);
          setProgressMessage("");
        };
      });
    },
    onSuccess: async (data, variables) => {
      // Ensure the conversation is updated with the latest data
      const kind = variables.kind ?? currentKind;
      const conversationId = data.id;
      queryClient.setQueryData(["chat", conversationId], data);
      setCurrentConversationForKindIfActive(kind, conversationId, data);
    },
    onError: () => {
      setIsStreamingResponse(false);
      setProgressMessage("");
    },
  });

  const value = {
    currentConversation,
    currentConversationByKind,
    currentKind,
    setCurrentKind,
    getCurrentConversation,
    setCurrentConversationForKind,
    loadConversation,
    isSidebarOpen,
    toggleSidebar,
    updateTitle,
    chats,
    setChats: (chats: ChatConversation[] | ((prevChats: ChatConversation[]) => ChatConversation[])) => {
      queryClient.setQueryData(["chats"], chats);
    },
    isLoading,
    createChat: (municipalityId: number, addressData?: Record<string, unknown>, kind?: ChatKind) =>
      createChatMutation.mutateAsync({ municipalityId, addressData, kind }),
    isCreatingChat: createChatMutation.isPending,
    sendPrompt: async (prompt: string, selectedDocumentIds?: number[], kind?: ChatKind) => {
      await sendPromptMutation.mutateAsync({ prompt, documentIds: selectedDocumentIds, kind });
    },
    isSendingPrompt: sendPromptMutation.isPending,
    isStreamingResponse,
    progressMessage,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
}
